<template>
  <ui-modal-window
    :show="showDealerSelector"
    class="emh-dealer-selection-modal"
    data-test-id="emh-dealer-selection-modal"
    :mode="mode"
    :headline="headline"
    :close-on-backdrop-click="closeOnBackdropClick"
    :close-on-escape-key="closeOnEscapeKey"
    @close="onClose"
  >
    <template #content>
      {{ description }}
      <dealer-locator-selector
        :interactive="true"
        :vendors="dealerIds"
        :allowed-list="allowedList"
        :apply-allowed-list-enabled="applyAllowedListEnabled"
        loading="eager"
        data-test-id="emh-dealer-selection-modal__dealer-selector"
        @vendor-selected-all="selectVendor"
      />
    </template>
  </ui-modal-window>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({ name: 'DealerSelectionModal' });
</script>

<script lang="ts" setup>
import DealerLocatorSelector from '../utils/dealerLocatorSelector/index.vue';
import UiModalWindow from '@dh-io-emh/ui/src/components/modalWindow/ModalWindow.vue';
import { defineProps, nextTick, PropType, ref, toRefs, watch } from 'vue';
import { DealerInfo, IDealerAddress } from '../../models/dealer.model';
import { useTracking } from '../../composables/tracking.composable';

const props = defineProps({
  headline: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  selectedDealer: {
    type: Object as PropType<DealerInfo | null>,
    default: null,
  },
  dealerIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  allowedList: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  applyAllowedListEnabled: {
    type: Boolean,
    default: false,
  },
  forceSelection: {
    type: Boolean,
    default: false,
  },
  show: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'info',
  },
  closeOnBackdropClick: {
    type: Boolean,
    default: true,
  },
  closeOnEscapeKey: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['vendor-selected', 'close']);

const { selectedDealer, forceSelection, show: showProp } = toRefs(props);

const { trackLinkClickOnce, trackLinkClick } = useTracking();

const showDealerSelector = ref(
  showProp.value || (forceSelection.value && Object.keys(selectedDealer?.value || {}).length === 0),
);

const selectVendor = (vendor: IDealerAddress): void => {
  emit('vendor-selected', vendor);
  showDealerSelector.value = false;
};

const onClose = () => {
  showDealerSelector.value = false;
  checkDealer(selectedDealer?.value);
};

const checkDealer = (vendor: DealerInfo | null) => {
  nextTick(() => {
    if (!vendor && forceSelection.value) {
      trackLinkClickOnce('dealer_selection_tried_to_close', {
        eventDetail: {
          category: 'feature',
          action: 'dealer_selection',
          label: 'tried_to_close',
        },
      });

      // force mode active and no dealer selected, open modal again
      showDealerSelector.value = true;
    } else {
      if (forceSelection.value) {
        trackLinkClick({
          eventDetail: {
            category: 'feature',
            action: 'dealer_selection',
            label: 'successfully_closed',
          },
        });
      }

      emit('close');
    }
  });
};

watch(selectedDealer, checkDealer);
watch(showProp, () => {
  showDealerSelector.value = forceSelection.value ? showDealerSelector.value : showProp.value;
});

watch(
  showDealerSelector,
  () => {
    if (showDealerSelector.value && forceSelection.value) {
      trackLinkClickOnce('dealer_selection_modal', {
        eventDetail: {
          category: 'impression',
          action: 'dealer_selection',
          label: 'modal',
        },
      });
    }
  },
  { immediate: true },
);
</script>
